import type { TooltipContentProps } from "recharts";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ClientOnly } from "~/components/ui/client-only";
import { formatCurrency } from "~/lib/format";
import type { YearSummary } from "~/types/financials";

interface BalanceTrendChartProps {
	years: YearSummary[];
}

function BalanceTooltip({ active, payload, label }: TooltipContentProps) {
	if (!active || !payload?.length) return null;
	const totalAssets = payload.find((item) => item.dataKey === "totalAssets")?.value;
	const equity = payload.find((item) => item.dataKey === "equity")?.value;

	return (
		<div className="glass-panel-strong min-w-44 rounded-xl px-3 py-2.5 text-xs">
			<p className="font-semibold text-text">Rok {String(label)}</p>
			<div className="mt-2 flex flex-col gap-1.5">
				<div className="flex items-center justify-between gap-4">
					<span className="text-muted">Aktiva</span>
					<span className="font-mono font-semibold tabular-nums text-chart-1">
						{formatCurrency(typeof totalAssets === "number" ? totalAssets : null)}
					</span>
				</div>
				<div className="flex items-center justify-between gap-4">
					<span className="text-muted">Vlastní kapitál</span>
					<span className="font-mono font-semibold tabular-nums text-chart-2">
						{formatCurrency(typeof equity === "number" ? equity : null)}
					</span>
				</div>
			</div>
		</div>
	);
}

export function BalanceTrendChart({ years }: BalanceTrendChartProps) {
	const data = years.map((year) => ({
		year: year.year.toString(),
		totalAssets: year.totalAssets,
		equity: year.equity,
	}));
	if (!data.some((year) => year.totalAssets != null || year.equity != null)) return null;

	return (
		<div className="card-surface overflow-hidden rounded-2xl p-6">
			<div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
				<div>
					<p className="label-overline">Vývoj rozvahy</p>
					<h3 className="mt-1 text-lg font-semibold">Vývoj aktiv a vlastního kapitálu</h3>
				</div>
				<div className="flex w-fit items-center gap-4 text-xs font-medium text-muted">
					<span className="flex items-center gap-1.5">
						<span className="h-2.5 w-2.5 rounded-sm bg-chart-1" /> Aktiva
					</span>
					<span className="flex items-center gap-1.5">
						<span className="h-2.5 w-2.5 rounded-sm bg-chart-2" /> Vlastní kapitál
					</span>
				</div>
			</div>
			<div className="mt-4 [&_.recharts-surface]:outline-none [&_.recharts-surface:focus-visible]:ring-2 [&_.recharts-surface:focus-visible]:ring-primary-500 [&_.recharts-surface:focus-visible]:ring-offset-2">
				<ClientOnly fallback={<div className="h-[300px] animate-pulse rounded bg-muted" />}>
					<ResponsiveContainer width="100%" height={320}>
						<BarChart
							data={data}
							barCategoryGap="32%"
							barGap={4}
							margin={{ top: 16, right: 8, bottom: 8, left: 8 }}
							title="Vývoj aktiv a vlastního kapitálu"
							desc="Srovnání aktiv a vlastního kapitálu v jednotlivých letech"
						>
							<CartesianGrid
								vertical={false}
								strokeDasharray="3 3"
								stroke="var(--color-border)"
								strokeOpacity={0.8}
							/>
							<XAxis
								dataKey="year"
								axisLine={false}
								tickLine={false}
								tick={{ fill: "var(--color-muted)", fontSize: 12 }}
								dy={10}
							/>
							<YAxis
								axisLine={false}
								tickLine={false}
								tick={{ fill: "var(--color-muted)", fontSize: 11 }}
								tickFormatter={(value: number) => formatCurrency(value)}
								width={92}
							/>
							<Tooltip content={BalanceTooltip} cursor={false} />
							<Bar
								dataKey="totalAssets"
								fill="var(--color-chart-1)"
								maxBarSize={42}
								radius={[6, 6, 0, 0]}
							/>
							<Bar
								dataKey="equity"
								fill="var(--color-chart-2)"
								maxBarSize={42}
								radius={[6, 6, 0, 0]}
							/>
						</BarChart>
					</ResponsiveContainer>
				</ClientOnly>
			</div>
		</div>
	);
}
